import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import classNames from "classnames";
import { Icons } from "../Icons";
import { useAuth } from "../../lib/auth/useAuth";
import { useRole } from "../../lib/auth/useRole";
import { getDisplayName, getInitials } from "../../lib/session";
import { isNavActive, navForRole, type NavItem } from "../../lib/nav";
import { KitStatus } from "./KitStatus";

interface Props {
  open?: boolean;
  onClose?: () => void;
}

export function Sidebar({ open, onClose }: Props) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const role = useRole();
  const { primary, more } = navForRole(role);
  const displayName = getDisplayName(user);
  const email = (user?.profile as { email?: string } | undefined)?.email;
  const initials = getInitials(displayName);

  const inMore = more.some((item) => isNavActive(item, pathname));
  const [showMore, setShowMore] = useState(inMore);
  useEffect(() => {
    if (inMore) setShowMore(true);
  }, [inMore]);

  const link = (item: NavItem) => {
    const active = isNavActive(item, pathname);
    return (
      <Link
        key={item.id}
        to={item.path}
        className={classNames("nav-item", { active })}
        aria-current={active ? "page" : undefined}
        title={item.label}
        onClick={onClose}
      >
        <span className="ic-wrap">
          <item.icon size={19} sw={1.8} />
        </span>
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <nav
      className={classNames("sidebar", { open })}
      aria-label="Navigation principale"
    >
      <div className="brand-mark">
        <div className="logo">C</div>
        <div>
          <div className="name">Chronos</div>
        </div>
      </div>

      <div className="nav-list">
        <div className="nav-section">Temps</div>
        {primary.map(link)}

        <button
          type="button"
          className="nav-item nav-more-toggle"
          aria-expanded={showMore}
          aria-controls="nav-more"
          title="Plus"
          onClick={() => setShowMore((v) => !v)}
        >
          <span className="ic-wrap">
            <Icons.more size={19} sw={1.8} />
          </span>
          <span>Plus</span>
          <Icons.chevD
            className={classNames("nav-more-chev", { open: showMore })}
            size={14}
            sw={2}
          />
        </button>
        <div id="nav-more" className="nav-more" hidden={!showMore}>
          {more.map(link)}
        </div>
      </div>

      <div className="sidebar-spacer" />

      <KitStatus canEnroll={role === "admin"} onNavigate={onClose} />

      <div className="user-card">
        <NavLink to="/profil" className="user-card-link" onClick={onClose}>
          <div className="av">{initials || "?"}</div>
          <div className="info">
            <div className="nm">{displayName}</div>
            {email && <div className="rl">{email}</div>}
          </div>
        </NavLink>
        <button
          className="menu"
          onClick={() => void logout()}
          title="Déconnexion"
          aria-label="Déconnexion"
        >
          <Icons.out size={16} sw={1.8} />
        </button>
      </div>
    </nav>
  );
}
